<template>
  <div class="container">
    <!-- 서브상단영역 -->
    <!-- Banner component was already here, no changes needed for it based on OrgForm.vue's Banner usage for this request -->
    <Loading v-show="loading" />
    <Banner
      title=""
      subtitle=""
      description="학생들과 함께 그들의 미래를 찾는 일을 하고자 합니다. <br>
        자신의 미래에 관심이 있는 학생 누구에게나 도움을 주고자 합니다."
    />

    <!-- //메뉴 -->
    <div class="width_wrap">
      <div class="body_content">
        <!-- 서브 타이틀영역 -->
        <h1 class="sub_subject">[기관] 신규기관등록 신청<span></span></h1>
        <n-form
          ref="registrationFormRef"
          :model="registrationModel"
          :rules="registrationRules"
          label-placement="left"
          label-width="auto"
          require-mark-placement="right-hanging"
        >
          <n-grid :cols="3" :x-gap="20">
            <n-form-item-gi label="기관 ID" path="uid" span="3">
              <n-input-group>
                <n-input
                  v-model:value="registrationModel.uid"
                  placeholder="영문, 숫자 조합 (예: coko)"
                  style="width: 300px"
                />
                <n-button
                  type="primary"
                  ghost
                  @click="checkOrganizationId"
                  :loading="isCheckingId"
                  >중복 체크</n-button
                >
              </n-input-group>
              <div
                v-if="idCheckMessage"
                :class="isIdAvailable ? 'success-message' : 'error-message'"
                style="width: 100%"
              >
                {{ idCheckMessage }}
              </div>
            </n-form-item-gi>
            <n-form-item-gi label="기관명" path="name">
              <n-input
                v-model:value="registrationModel.name"
                placeholder="기관 전체 이름"
              />
            </n-form-item-gi>
            <n-form-item-gi label="대표자 이름" path="president">
              <n-input
                v-model:value="registrationModel.president"
                placeholder="대표자 이름"
              />
            </n-form-item-gi>
            <n-form-item-gi label="기관 유형" path="type">
              <n-select
                v-model:value="registrationModel.type"
                placeholder="기관 유형 선택"
                :options="typeOptions"
              />
            </n-form-item-gi>
            <n-form-item-gi label="우편번호" path="address.postCode">
              <n-input-group>
                <n-input
                  v-model:value="registrationModel.address.postCode"
                  placeholder="우편번호"
                  :disabled="true"
                  class="force-black-text-disabled"
                />
                <n-button
                  type="primary"
                  ghost
                  @click="openPostcodeSearch"
                  style="margin-left: 5px"
                >
                  주소검색
                </n-button>
              </n-input-group>
            </n-form-item-gi>
            <n-form-item-gi label="주소" path="address.address" span="2">
              <n-input
                v-model:value="registrationModel.address.address"
                placeholder="주소"
                :disabled="true"
                class="force-black-text-disabled"
              />
            </n-form-item-gi>
            <n-form-item-gi
              label="상세주소"
              path="address.addressDetail"
              span="3"
            >
              <n-input
                id="addressDetailInput"
                v-model:value="registrationModel.address.addressDetail"
                placeholder="상세주소"
              />
            </n-form-item-gi>
            <n-form-item-gi label="담당부서" path="dept">
              <n-input
                v-model:value="registrationModel.dept"
                placeholder="담당부서 이름"
              />
            </n-form-item-gi>
            <n-form-item-gi label="전화번호" path="phoneNumber">
              <n-input
                v-model:value="registrationModel.phoneNumber"
                placeholder="대표 전화번호 (예: 02-1234-5678)"
              />
            </n-form-item-gi>
            <n-form-item-gi> &nbsp; </n-form-item-gi>
            <n-form-item-gi label="담당자 이름" path="deptPersonName">
              <n-input
                v-model:value="registrationModel.deptPersonName"
                placeholder="담당자 이름"
              />
            </n-form-item-gi>
            <n-form-item-gi label="담당자 연락처" path="deptPersonName">
              <n-input
                v-model:value="registrationModel.deptPersonPhoneNumber"
                placeholder="담당자 전화번호 (예: 010-1234-5678)"
              />
            </n-form-item-gi>
            <n-form-item-gi label="담당자 이메일" path="email">
              <n-input
                v-model:value="registrationModel.email"
                placeholder="대표 이메일 주소"
              />
            </n-form-item-gi>
            <n-form-item-gi label="설명" path="description" span="3">
              <n-input
                v-model:value="registrationModel.description"
                type="textarea"
                placeholder="기관에 대한 간략한 설명 (선택 사항)"
              />
            </n-form-item-gi>
          </n-grid>
        </n-form>
        <div class="registration-form-content">
          <div style="text-align: right; margin-top: 20px">
            <n-button
              @click="cancelRequest"
              type="tertiary"
              style="margin-right: 10px"
              >취소</n-button
            >
            <n-button type="primary" @click="handleRegistrationRequestSubmit"
              >등록 요청</n-button
            >
            <!-- 실제 등록 로직 연결 필요 -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Organization, OrganizationType } from "@/model/organization";
import { ref } from "vue";
import { getCallable } from "@/config/env";
import {
  NButton,
  NForm,
  NFormItem,
  NInput,
  NInputGroup,
  NSelect,
  FormInst,
  FormRules,
  useMessage,
  useNotification,
} from "naive-ui";
import { useRouter } from "vue-router";
import * as common from "@/assets/common/common";
import { USER_ROUTES } from "@/assets/route_constants";
import Banner from "@/components/Banner.vue"; // Added Banner import
import Loading from "@/components/Loading.vue"; // Added Loading import

const loading = ref(false); // Added loading state

const message = useMessage();
const notification = useNotification();
const router = useRouter();

const createOrganizationRequestCallable = getCallable(
  "createOrganizationRequest"
);
const checkOrgIdCallable = getCallable("checkOrgId");

const isCheckingId = ref<boolean>(false);
const registrationFormRef = ref<FormInst | null>(null);
const registrationModel = ref<Partial<Organization>>({
  uid: "",
  name: "",
  president: "",
  address: {
    // postCode, address, addressDetail will be filled by Daum Postcode API
    postCode: "",
    address: "",
    addressDetail: "",
  },
  dept: "",
  deptPersonName: "",
  deptPersonPhoneNumber: "",
  email: "",
  phoneNumber: "",
  description: "",
  type: null,
});
const idCheckMessage = ref<string>("");
const isIdAvailable = ref<boolean | null>(null);

const typeOptions = ref([
  {
    label: "초등학교",
    value: OrganizationType.ElementarySchool,
  },
  { label: "중학교", value: OrganizationType.MiddleSchool },
  { label: "고등학교", value: OrganizationType.HighSchool },
  { label: "대학교", value: OrganizationType.University },
  { label: "기타 기관(법인/재단 등)", value: OrganizationType.Etc },
]);

const registrationRules: FormRules = {
  uid: [
    {
      required: true,
      message: "기관 ID를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  name: [
    {
      required: true,
      message: "기관명을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  president: [
    {
      required: true,
      message: "대표자 이름을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  dept: [
    {
      required: true,
      message: "담당부서를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  deptPersonName: [
    {
      required: true,
      message: "담당자 이름을 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  deptPersonPhoneNumber: [
    {
      required: true,
      message: "담당자 연락처를 입력해주세요.",
      trigger: ["input", "blur"],
    },
  ],
  email: [
    {
      required: true,
      message: "이메일을 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      type: "email",
      message: "올바른 이메일 형식이 아닙니다.",
      trigger: ["blur"],
    },
  ],
  phoneNumber: [
    {
      required: true,
      message: "전화번호를 입력해주세요.",
      trigger: ["input", "blur"],
    },
    {
      pattern: /^\d{2,3}-\d{3,4}-\d{4}$/,
      message:
        "전화번호 형식이 올바르지 않습니다. (예: 02-123-4567 또는 010-1234-5678)",
      trigger: ["blur"],
    },
  ],
  type: [
    { required: true, message: "기관 유형을 선택해주세요.", trigger: "change" },
  ],
};

const openPostcodeSearch = () => {
  if (
    typeof (window as any).daum === "undefined" ||
    typeof (window as any).daum.Postcode === "undefined"
  ) {
    message.error(
      "Daum 우편번호 서비스가 로드되지 않았습니다. 페이지를 새로고침하거나 관리자에게 문의하세요."
    );
    return;
  }
  new (window as any).daum.Postcode({
    oncomplete: (data: any) => {
      let addr = ""; // 주소 변수
      let extraAddr = ""; // 참고항목 변수

      if (data.userSelectedType === "R") {
        addr = data.roadAddress;
      } else {
        addr = data.jibunAddress;
      }

      if (data.userSelectedType === "R") {
        if (data.bname !== "" && /[동|로|가]$/g.test(data.bname)) {
          extraAddr += data.bname;
        }
        if (data.buildingName !== "" && data.apartment === "Y") {
          extraAddr +=
            extraAddr !== "" ? ", " + data.buildingName : data.buildingName;
        }
        if (extraAddr !== "") {
          extraAddr = " (" + extraAddr + ")";
        }
      }

      if (registrationModel.value.address) {
        registrationModel.value.address.postCode = data.zonecode;
        registrationModel.value.address.address = addr + extraAddr;
      }
      document.getElementById("addressDetailInput")?.focus();
    },
  }).open();
};

async function checkOrganizationId() {
  if (!registrationModel.value.uid) {
    idCheckMessage.value = "기관 ID를 먼저 입력해주세요.";
    isIdAvailable.value = false;
    return;
  }
  isCheckingId.value = true; // ID 중복 체크 로딩 시작
  idCheckMessage.value = "";
  isIdAvailable.value = null;
  try {
    const result: any = await checkOrgIdCallable({
      id: registrationModel.value.uid,
    });
    if (common.evalFunctionResult(result)) {
      const checkResult = common.getItemFromFunctionResult(result);
      if (checkResult) {
        idCheckMessage.value = "이미 사용 중인 ID입니다.";
        isIdAvailable.value = false;
      } else {
        idCheckMessage.value = "사용 가능한 ID입니다.";
        isIdAvailable.value = true;
      }
    } else {
      idCheckMessage.value = "ID 중복 체크 중 오류가 발생했습니다.";
      isIdAvailable.value = false;
    }
  } catch (error: any) {
    console.error("ID 중복 체크 실패:", error);
    idCheckMessage.value = "ID 중복 체크 중 오류가 발생했습니다.";
    isIdAvailable.value = false;
  } finally {
    isCheckingId.value = false; // ID 중복 체크 로딩 종료
  }
}

async function handleRegistrationRequestSubmit() {
  try {
    await registrationFormRef.value?.validate();
  } catch (errors) {
    message.error("입력 내용을 확인해주세요.");
    return;
  }
  if (isIdAvailable.value !== true) {
    message.error("기관 ID 중복 체크를 통과해야 합니다.");
    return;
  }
  loading.value = true;
  try {
    const result = await createOrganizationRequestCallable(
      registrationModel.value
    );
    if (common.evalFunctionResult(result)) {
      message.success("기관 등록요청 성공! 검토 후 연락드리겠습니다.");
      router.push(USER_ROUTES.Home.Path); // Navigate to home or a success page
    } else {
      notification.error({
        title: "등록요청 에러",
        content: `기관 등록요청 실패: ${
          common.getResultError(result) || "오류가 발생했습니다."
        }`,
        duration: 1500,
        closable: true,
      });
    }
  } catch (error) {
    notification.error({
      title: "에러",
      content: `기관 등록요청 실패: ${error || "오류가 발생했습니다."}`,
      duration: 3000,
      closable: true,
    });
  } finally {
    loading.value = false;
  }
}

function cancelRequest() {
  router.back(); // Or clear form: registrationModel.value = { ...initialState };
}
</script>

<style scoped>
.success-message {
  color: green;
}

.error-message {
  color: #d8000c; /* 기존 에러 메시지 색상과 동일하게 */
}
.glores-B-error-msg {
  color: red;
  font-size: 0.9em;
  margin-top: 5px;
}
.glores-B-form-error {
  width: 100%;
  text-align: center;
  margin-top: 15px;
}
.registration-form-content p {
  margin: 8px 0;
  line-height: 1.6;
  color: #333; /* 모달 내부 텍스트 색상 명확히 */
}

.description-item-label-fixed-width {
  width: 100px; /* "기관 ID" 레이블과 유사한 너비로 조정 */
  display: inline-block; /* 너비 적용을 위해 */
}
.description-item-content-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.table-container {
  width: 100%; /* 부모 요소의 너비를 최대한 활용 */
  overflow-x: auto; /* 내용이 넘칠 경우 가로 스크롤바 표시 */
}

.force-black-text-disabled.n-input--disabled :deep(.n-input__input-el) {
  color: #000000 !important; /* 검은색으로 설정 */
  -webkit-text-fill-color: #000000 !important; /* WebKit 브라우저 호환성 */
  opacity: 1 !important; /* 비활성화 시 텍스트 투명도 제거 */
}
</style>
